/**
 * COMPENDIUM view-models — pure transforms from ({@link CompendiumSource}, {@link CompendiumState}) into
 * the render-ready shapes the window draws, split into the three panes: {@link computeBrowse} (rail + list/groups/facets),
 * {@link computeSearch} (omninet query results), and {@link buildDetail} (the record sheet).
 *
 * No React here — the component supplies handlers and binds these outputs to JSX.
 */
import {
  CompendiumSource,
  type CompendiumState,
  type CompendiumType,
  type CompendiumEntry,
  type Ref,
  type SectionId,
  SECTIONS,
  TYPE_LABEL,
  TYPE_SING,
  ROMAN,
  DMG_COL,
  MFR_ORDER,
  mfrStyle,
  tagLabel,
  pilotGearProfile,
  type Frame,
  type Weapon,
  type System,
  type Mod,
  type Talent,
  type Skill,
  type PilotGear,
  type CoreBonus,
  type Damage,
  type RangeVal,
  type NpcClass,
  type NpcTemplate,
  type NpcFeature,
} from "./compendiumData";
import type { TagRef } from "@lancer-vtt/compendium";
import type { Background } from "@lancer-vtt/compendium";
import { NPC_TIERS, asNpcTier, interpolateTierTokens, statAtTier, type NpcTier } from "../../lib/npcTierText";
import { npcWeaponView, type NpcWeaponView } from "./npcWeapon";
import { npcDeployableViews, type NpcDeployableView } from "./npcDeployable";
import { npcClassArtUrl } from "../../lib/tokenArt";

const GROUPABLE: CompendiumType[] = ["frame", "weapon", "system", "mod"];

// ---- facet axes ----------------------------------------------------------------------
//
// Each browsable type declares zero or more *facet axes* — independent filter dimensions,
// each a row of toggle chips. A type can carry several (a weapon chips by both mount AND SP,
// AND-combined). An axis pulls its candidate values off the normalized {@link CompendiumEntry}
// (so no per-row raw lookups). `valueOf` returns the value(s) a record exposes on that axis;
// a record matches an active facet when one of its axis values equals the selected value.

/** One filter dimension for a category — a labelled row of value chips. */
type FacetAxis = {
  /** Stable axis key, also the {@link CompendiumState.facets} map key (e.g. "maker", "mount"). */
  key: string;
  /** Short axis caption shown before the chip row (e.g. "MAKER", "MOUNT", "SP"). */
  label: string;
  /** The value(s) this record exposes on the axis — a record matches if one equals the selection. */
  valueOf: (e: CompendiumEntry) => string | string[] | undefined;
  /** Render a raw value into its chip caption (defaults to upper-cased value). */
  chipLabel?: (val: string) => string;
  /** Sort comparator for the chip order (defaults to first-seen order). */
  sort?: (a: string, b: string) => number;
};

const byNum = (a: string, b: string) => Number(a) - Number(b);
const makerAxis: FacetAxis = { key: "maker", label: "MAKER", valueOf: (e) => e.source };

/**
 * The facet axes per category. Maker/role are single-axis; weapons are multi-axis
 * (mount + SP) and mods carry a multi-value `allowed_types` fit axis. Only these types facet;
 * everything else returns none (no chip row).
 */
const FACET_AXES: Partial<Record<CompendiumType, FacetAxis[]>> = {
  frame: [
    makerAxis,
    { key: "ll", label: "LL", valueOf: (e) => (e.license_level != null ? String(e.license_level) : undefined), chipLabel: (v) => `LL ${v}`, sort: byNum },
  ],
  weapon: [
    makerAxis,
    { key: "mount", label: "MOUNT", valueOf: (e) => e.mount },
    { key: "sp", label: "SP", valueOf: (e) => (e.sp != null ? String(e.sp) : undefined), chipLabel: (v) => `${v} SP`, sort: byNum },
  ],
  system: [makerAxis],
  mod: [
    makerAxis,
    { key: "fit", label: "FITS", valueOf: (e) => e.allowedTypes },
  ],
  npc_class: [{ key: "role", label: "ROLE", valueOf: (e) => e.role }],
};

/** True when a record's value(s) on an axis include the selected facet value. */
const axisMatches = (axis: FacetAxis, e: CompendiumEntry, sel: string): boolean => {
  const v = axis.valueOf(e);
  return Array.isArray(v) ? v.includes(sel) : v === sel;
};

export const sectionOf = (type: CompendiumType): SectionId => {
  for (const sec of Object.keys(SECTIONS) as SectionId[]) {
    if (SECTIONS[sec].some(([t]) => t === type)) return sec;
  }
  return "MECHS";
};

// ---- responsive layout ---------------------------------------------------------------

/**
 * The window's two layout breakpoints, in measured-content-width px (the inner `size.w`,
 * NOT the padded outer frame). The rail folds *first*: below {@link RAIL_FOLD_W} the
 * three-pane drops its category rail; below {@link COMPACT_W} the whole thing collapses to
 * a single stacked column (list, then detail-replaces-list). Per the spec's ~700 / ~900
 * targets, with the rail-fold sitting at the wide threshold so a true three-pane only shows
 * once there's room for all three panes.
 */
export const COMPACT_W = 700;
export const RAIL_FOLD_W = 900;

/** The render mode the window is in, derived purely from its measured width. */
export type CompendiumLayout = {
  /** Three-pane (rail · list · detail) vs single stacked column. `wide` everywhere above {@link COMPACT_W}. */
  wide: boolean;
  /** Whether the category rail shows — folds away first, below {@link RAIL_FOLD_W} (only meaningful when `wide`). */
  showRail: boolean;
};

/**
 * Derive the layout from the window's *actual* measured width — the single source of truth
 * for "wide vs compact" (there is no manual toggle; dragging the corner grip reflows the
 * panes live). Compact below {@link COMPACT_W}; rail folds between {@link COMPACT_W} and
 * {@link RAIL_FOLD_W}; full three-pane at or above {@link RAIL_FOLD_W}.
 */
export const layoutFor = (width: number): CompendiumLayout => ({
  wide: width >= COMPACT_W,
  showRail: width >= RAIL_FOLD_W,
});

// ---- list / browse pane -------------------------------------------------------------

export type SectionTab = { id: SectionId; count: number; on: boolean };
export type RailItem = { type: CompendiumType; label: string; count: number; on: boolean };
export type ListRow = { type: CompendiumType; id: string; name: string; sub: string; pack: string; on: boolean; bar: string; llTag: string };
export type FacetChip = { val: string; label: string; on: boolean };
/** A rendered facet axis: its caption + the toggle chips for the values present in this category. */
export type FacetAxisView = { key: string; label: string; chips: FacetChip[] };
export type MakerGroup = { name: string; accent: string; tint: string; n: number; items: ListRow[] };

export type BrowseView = {
  sections: SectionTab[];
  sectionName: SectionId;
  rail: RailItem[];
  canGroup: boolean;
  catLabel: string;
  catLabelLow: string;
  catUpper: string;
  listCount: number;
  facetAxes: FacetAxisView[];
  hasFacets: boolean;
  isEmptyCat: boolean;
  showGrouped: boolean;
  groups: MakerGroup[];
  listRows: ListRow[];
};

const secCount = (source: CompendiumSource, id: SectionId) => SECTIONS[id].reduce((a, [t]) => a + source.count(t), 0);

function row(source: CompendiumSource, type: CompendiumType, ctx: { e: CompendiumEntry; selected: Ref | null }): ListRow {
  const { e, selected } = ctx;
  const on = selected != null && selected.type === type && selected.id === e.id;
  return {
    type,
    id: e.id,
    name: e.name,
    sub: source.subFor(type, e),
    pack: e.pack,
    on,
    bar: on ? source.accentOf(type, e.source) : "transparent",
    llTag: e.license_level != null ? `LL ${e.license_level}` : "",
  };
}

export function computeBrowse(source: CompendiumSource, s: CompendiumState): BrowseView {
  const sections: SectionTab[] = (Object.keys(SECTIONS) as SectionId[]).map((id) => ({
    id,
    count: secCount(source, id),
    on: id === s.section,
  }));

  const rail: RailItem[] = SECTIONS[s.section].map(([type, label]) => ({
    type,
    label,
    count: source.count(type),
    on: type === s.category,
  }));

  const cat = s.category;
  const all = source.list(cat);
  const fl = s.filter.trim().toLowerCase();
  const axes = FACET_AXES[cat] ?? [];

  // A record passes the active facets when it matches the selected value on EVERY axis that
  // has a selection (axes are independent and AND-combined — e.g. a 1-SP Auxiliary weapon).
  const passesFacets = (it: CompendiumEntry) =>
    axes.every((axis) => {
      const sel = s.facets[axis.key];
      return sel == null || axisMatches(axis, it, sel);
    });

  const items = all.filter((it) => {
    if (!passesFacets(it)) return false;
    if (!fl) return true;
    return it.name.toLowerCase().includes(fl) || it.id.toLowerCase().includes(fl);
  });

  // Per-axis chip rows: collect each axis's distinct present values off the full category list,
  // ordered by the axis comparator (or first-seen). An axis with ≤1 value contributes no chips.
  const facetAxes: FacetAxisView[] = [];
  for (const axis of axes) {
    const present: string[] = [];
    for (const it of all) {
      const v = axis.valueOf(it);
      for (const raw of Array.isArray(v) ? v : v != null ? [v] : []) {
        if (!present.includes(raw)) present.push(raw);
      }
    }
    if (axis.sort) present.sort(axis.sort);
    if (present.length < 2) continue;
    const sel = s.facets[axis.key];
    facetAxes.push({
      key: axis.key,
      label: axis.label,
      chips: present.map((val) => ({ val, label: (axis.chipLabel ?? ((x: string) => x.toUpperCase()))(val), on: sel === val })),
    });
  }
  const hasFacets = facetAxes.length > 0;

  // group by maker
  const canGroup = GROUPABLE.includes(cat);
  const showGrouped = canGroup && s.groupBy;
  const groups: MakerGroup[] = [];
  if (showGrouped) {
    const order = [...MFR_ORDER, ...source.manufacturers.map((m) => m.id).filter((id) => !MFR_ORDER.includes(id))];
    for (const src of order) {
      const gi = items.filter((it) => it.source === src);
      if (!gi.length) continue;
      const m = source.manufacturer(src);
      const style = mfrStyle(src);
      groups.push({
        name: (m?.name ?? src).toUpperCase(),
        accent: style.accent,
        tint: style.tint,
        n: gi.length,
        items: gi.map((e) => row(source, cat, { e, selected: s.selected })),
      });
    }
  }

  return {
    sections,
    sectionName: s.section,
    rail,
    canGroup,
    catLabel: TYPE_LABEL[cat],
    catLabelLow: TYPE_LABEL[cat].toLowerCase(),
    catUpper: TYPE_LABEL[cat].toUpperCase(),
    listCount: items.length,
    facetAxes,
    hasFacets,
    isEmptyCat: all.length === 0,
    showGrouped,
    groups,
    listRows: items.map((e) => row(source, cat, { e, selected: s.selected })),
  };
}

// ---- roving keyboard focus over the active list ---------------------------------------
//
// The list pane has three shapes — a flat `listRows`, a maker-grouped `groups[].items`, and
// (when searching) the search-results `groups[].items`. Keyboard arrow-nav must walk a single
// roving cursor across whichever shape is showing, so we flatten each into one ordered `Ref[]`.
// The window holds the cursor as a small `focusIdx` in its own UI state (NOT reducer/URL state):
// arrows move it within bounds, Enter maps the focused ref → a `select`. One focus model,
// uniform across all three list shapes.

/**
 * The active list as a single ordered sequence of record refs — the order rows render in,
 * regardless of shape (flat list, maker-grouped, or search results). Roving keyboard focus
 * walks this so one cursor spans all three shapes. Returns `[]` for an empty/grouped-into-
 * nothing list.
 */
export function orderedRefs(view: BrowseView | SearchView): Ref[] {
  if ("groups" in view && "listRows" in view) {
    // BrowseView: grouped uses the group order; flat uses listRows.
    if (view.showGrouped) return view.groups.flatMap((g) => g.items.map((it) => ({ type: it.type, id: it.id })));
    return view.listRows.map((it) => ({ type: it.type, id: it.id }));
  }
  // SearchView: results grouped by type, in group order.
  return (view as SearchView).groups.flatMap((g) => g.items.map((it) => ({ type: it.type, id: it.id })));
}

/**
 * Clamp a roving-focus index move. Arrow up/down shift by ±1 and stop at the ends (no wrap —
 * a long list shouldn't teleport top↔bottom on an over-press). An out-of-range or `-1` current
 * index snaps to the first row on a downward move and the last on an upward move, so the first
 * arrow press after a fresh list always lands somewhere visible.
 */
export function moveFocus(len: number, idx: number, delta: number): number {
  if (len <= 0) return -1;
  if (idx < 0 || idx >= len) return delta > 0 ? 0 : len - 1;
  return Math.min(len - 1, Math.max(0, idx + delta));
}

// ---- search pane --------------------------------------------------------------------

export type SearchHit = { type: CompendiumType; id: string; name: string; sub: string; dot: string };
export type SearchGroup = { label: string; n: number; items: SearchHit[] };
export type SearchView = { echo: string; summary: string; groups: SearchGroup[]; total: number };

const SEARCH_TYPES: CompendiumType[] = [
  "frame", "weapon", "system", "mod", "talent", "skill", "pilot_gear", "license", "background", "manufacturer",
  "npc_class", "npc_template", "npc_feature", "tag",
];

export function computeSearch(source: CompendiumSource, query: string): SearchView {
  const q = query.trim().toLowerCase();
  const groups: SearchGroup[] = [];
  let total = 0;
  for (const tt of SEARCH_TYPES) {
    const hits = source.list(tt).filter((it) => it.name.toLowerCase().includes(q) || it.id.toLowerCase().includes(q));
    if (!hits.length) continue;
    total += hits.length;
    groups.push({
      label: TYPE_LABEL[tt].toUpperCase(),
      n: hits.length,
      items: hits.map((e) => ({ type: tt, id: e.id, name: e.name, sub: source.subFor(tt, e), dot: source.accentOf(tt, e.source) })),
    });
  }
  return { echo: query.trim(), summary: `${total} RECORD${total === 1 ? "" : "S"}`, total, groups };
}

// ---- detail pane --------------------------------------------------------------------

export type DetailChip = { t: string; bd: string; fg: string; bg: string };
export type LinkItem = { type: CompendiumType; id: string; name: string; kind: string; dot: string };
/**
 * One activated action on a system/mod (a Lancer item's `actions[]` entry). The mechanical
 * guts of many systems live here, NOT in the flat `effect` string — e.g. StunCrown's whole
 * effect is a Quick action. `detailHtml` is the rules prose (it already spells out any
 * status/save the action applies); `activation`/`frequency`/`trigger` are the framing chips.
 */
export type ActionView = { name?: string; activation?: string; frequency?: string; triggerHtml?: string; detailHtml?: string };
/**
 * One selectable ammunition type on a system's `ammo[]` (the Walking Armory Ammo Cases are the
 * only carriers in core). These ARE the "following effects" the case's `effect` string points at —
 * without them the record reads as a promise with nothing behind it. `cost` is the charge price;
 * lancer-data omits it on the 1-charge options, so it's normalized here rather than at render.
 */
export type AmmoView = { name: string; cost: number; detailHtml?: string };
/**
 * One firing profile of a multi-profile weapon (`weapons.json`'s `profiles[]`). Five core weapons
 * carry one — Assault Cannon / Leviathan / Siege Cannon (Standard vs. a wound-up mode), the
 * Barbarossa's Apocalypse Rail, the Sherman's ZF4 SOLIDCORE (four Charge steps) — and for all but
 * the Raleigh's the profiles are the ONLY place the damage/range live: those weapons have no
 * top-level `damage`/`range` at all, so a detail pane that reads only the top level renders them
 * with two empty stat panels. Each profile can also redefine its own tags (the Assault Cannon is
 * Overkill in Standard mode and Reliable while spun up), so tags are per-profile, not shared.
 */
export type WeaponProfileView = {
  name: string;
  damage: { type: string; val: string | number; col: string }[];
  range: { type: string; val: string | number }[];
  effectHtml?: string;
  tags: { id: string; label: string }[];
};
/**
 * One `on_attack` / `on_hit` / `on_crit` **triggered clause** — a rider that fires only under its
 * stated condition, and is emphatically NOT part of the weapon's ordinary on-hit damage. 25 weapons
 * and one mod (Thermal Charge) carry one across core + Long Rim — 28 clauses in all — and every
 * single one of them was being dropped by the detail pane: the branches read
 * `damage`/`range`/`effect`/`profiles` and nothing else.
 *
 * lancer-data spells the clause two ways — a bare prose string (19 of the 28), or an object whose
 * `detail` is that same prose plus a structured rider: `damage` (Gravity Gun, Thermal Charge),
 * `add_status` (Concussion Missiles, Power Knuckles, Catalytic Hammer, Impaler Nailgun, Burst
 * Launcher), `save` (Magnetic Cannon), `bonus_damage` (Variable Sword), `activation` (Thermal
 * Charge's free-action detonator). Both shapes normalize here.
 *
 * Only `damage` is lifted into pips — it's the one rider whose numbers have nowhere else to land
 * (the Gravity Gun has NO top-level `damage` at all, so its whole offensive output lived in this
 * clause and its DAMAGE panel rendered empty). `add_status` / `save` / `bonus_damage` are left to
 * the prose, which states each of them in full ("…must succeed on a Hull save or become Impaired
 * until the end of their next turn", "Deal +1d6 bonus damage") — pilling them would duplicate the
 * sentence, and `bonus_damage` carries no damage type to colour a pip with.
 *
 * {@link quals} is what keeps the block honest: a save-gated AoE rider must never read like a
 * plain damage line, so the gating conditions the structured fields declare (`save: "hull"`,
 * `aoe: true`) render as chips beside the pips.
 */
export type TriggeredClauseView = {
  /** The trigger, display-cased for the chip: `"ON ATTACK"` / `"ON HIT"` / `"ON CRIT"`. */
  trigger: string;
  /** The clause's rules prose. Omitted when it merely repeats the record's own `effect` string. */
  detailHtml?: string;
  /** The clause's activation cost, when it has one (only Thermal Charge's `Free` in core). */
  activation?: string;
  /** The firing profile this clause belongs to — set only when it isn't shared by all of them. */
  profile?: string;
  /** Damage the clause itself deals, pipped exactly like the record's own DAMAGE panel. */
  damage: { type: string; val: string | number; col: string }[];
  /** The conditions gating the clause, from the structured fields — e.g. `"HULL SAVE"`, `"AOE"`. */
  quals: string[];
};

export type StatCell = { k: string; v: number | string };
/**
 * One labelled band of the frame stat block. The frame sheet groups its stats by *what mechanic
 * reads the number* rather than printing all fourteen as one undifferentiated grid — DURABILITY
 * (what you absorb + recover), DEFENSES (the numbers an opponent's roll is compared against),
 * SYSTEMS (heat ceiling + tech profile), CHASSIS (footprint + build budget). Rendered as labelled
 * rows sharing the MOUNTS row's left-gutter idiom.
 */
export type StatGroup = { label: string; cells: StatCell[] };
/** A tiered NPC stat row: a label plus its tier-1/2/3 values (NPC stats scale by class tier). */
/**
 * One row of the NPC stat array at the SELECTED tier — a single value, not three columns.
 * The developer's call (2026-09-05) was a T1/T2/T3 toggle showing one tier at a time rather than
 * side-by-side tier columns. `varies` is true when the other tiers would read differently, so the
 * sheet can mark the numbers the toggle actually moves.
 */
export type NpcStatRow = { k: string; v: string; varies: boolean };
/** A named group of cross-linked NPC features (e.g. "BASE" / "OPTIONAL"). */
export type FeatureGroup = { label: string; items: LinkItem[] };

export type Detail = {
  kind: CompendiumType;
  name: string;
  pack: string;
  accent: string;
  /**
   * Whether this record's header recolors to its manufacturer accent — true only when the
   * source is reliably a real maker (frame/weapon always; system/mod/license when `source` is
   * set). Non-maker records (talents, tags, NPCs, backgrounds, …) keep the ink-black header.
   * Drives the `<ThemeScope>`-fed `var(--editor-accent)` header background.
   */
  tinted: boolean;
  ref: string;
  kicker: string;
  headBg: string;
  headFg: string;
  headSub: string;
  chips: DetailChip[];
  descHtml?: string;
  effectHtml?: string;
  detailHtml?: string;
  /** A core bonus's `mounted_effect` — the bonus's mech-active clause, shown as its own section. */
  mountedHtml?: string;
  quoteHtml?: string;
  mfrId?: string;
  mfrName?: string;
  /** Hero art URL: a frame's chassis art (`public/frames/`), or an NPC class's art (`public/tokens/npc/`). */
  artUrl?: string;
  statGroups?: StatGroup[];
  /**
   * Structure + Stress, split out below the grouped grid. They are `4`/`4` on every core frame
   * (zero variance across all 28), so they carry no comparison signal and are shown as a quiet
   * footer line — kept rather than dropped because a third-party LCP may set them otherwise.
   */
  statTracks?: StatCell[];
  mounts?: string[];
  damage?: { type: string; val: string | number; col: string }[];
  range?: { type: string; val: string | number }[];
  tags?: { id: string; label: string }[];
  allowed?: string[];
  ranks?: { roman: string; name: string; descHtml: string }[];
  actions?: ActionView[];
  ammo?: AmmoView[];
  unlocks?: { rank: string; items: LinkItem[] }[];
  profile?: { k: string; v: string | number }[];
  /** A weapon's firing profiles — present only when the record carries `profiles[]`. */
  weaponProfiles?: WeaponProfileView[];
  /** A weapon's/mod's `on_attack`/`on_hit`/`on_crit` riders (see {@link TriggeredClauseView}). */
  triggered?: TriggeredClauseView[];
  refs?: LinkItem[];
  catalog?: LinkItem[];
  bgSkills?: LinkItem[];
  // NPC fields
  npcRole?: string;
  npcType?: string;
  npcStats?: NpcStatRow[];
  /** The tier this NPC record was built at — what the sheet's T1/T2/T3 toggle should show as on. */
  npcTier?: NpcTier;
  npcFlavorHtml?: string;
  npcTacticsHtml?: string;
  /**
   * A v3 class's one-line summary (`info.terse`), e.g. "Fast and evasive striker that can deal
   * consistent damage." Measured distinct from `flavor`/`tactics`: it is a literal substring of
   * them on only 1 of 33 classes, so it is real unrendered content rather than a restatement.
   */
  npcTerse?: string;
  /** A template's `caveat` — a rules qualifier on when its traits function. Real text, v3-only. */
  npcCaveatHtml?: string;
  /**
   * The feature this one modifies (`mod`), as a navigable cross-link. 38 v3 features carry it, and
   * on 6 of them the prose never names the target at all — so the relationship is otherwise
   * invisible. On the other 32 the name appears in prose but is not clickable.
   */
  npcModifies?: LinkItem;
  featureGroups?: FeatureGroup[];
  /**
   * True when the pack marks this feature `deprecated` — a tombstone whose text only says where
   * the rule went. Developer's ruling 2026-09-06: these stay browsable and get badged, because a
   * GM who meets the name in an older stat block or a third-party module needs to look it up and
   * LEARN it is retired; suppressing them would hand that GM a blank result and no explanation.
   */
  npcDeprecated?: boolean;
  /**
   * A v3 NPC feature's `actions[]`, flattened for display. v3 moved feature rules text out of
   * `effect` and into this array, so a feature with no `effect` (171 of the 401 records in the
   * shipped v3 pack) has ALL of its rules text here — that is the blank-body defect. Prose is
   * already tier-interpolated by the time it lands here.
   */
  npcActions?: NpcActionView[];
  /**
   * The weapon stat block, tier-resolved. 54 features in EACH shipped pack are Weapons whose
   * `effect` is empty or absent, so this is their entire body.
   */
  npcWeapon?: NpcWeaponView;
  /**
   * Deployables this feature puts on the board. 13 v3 features have their whole content here and
   * nothing else at all — see `npcDeployable.ts`.
   */
  npcDeployables?: NpcDeployableView[];
};

/**
 * One entry of a v3 NPC feature's `actions[]`, normalized for rendering.
 *
 * Both prose keys are carried: `detail` is the usual one (138 of 401 records) and `effect` is a
 * second one six records use instead. Rendering only the common one would blank those six, which
 * is the exact failure mode this phase exists to remove, so both are kept and both render.
 */
export type NpcActionView = {
  name?: string;
  activation?: string;
  frequency?: string;
  trigger?: string;
  detailHtml?: string;
  effectHtml?: string;
};

const lightChip = (t: string): DetailChip => ({ t, bd: "rgba(255,255,255,.45)", fg: "#fff", bg: "transparent" });
const filledChip = (t: string): DetailChip => ({ t, bd: "rgba(255,255,255,.45)", fg: "#fff", bg: "rgba(255,255,255,.12)" });
const darkChip = (t: string): DetailChip => ({ t, bd: "#555", fg: "#fff", bg: "transparent" });
/**
 * A solid, unmissable badge — for a fact about the RECORD that changes how everything else on the
 * sheet should be read. Filled rather than outlined so it separates from the neutral type chips
 * beside it at a glance, without the reader parsing any text.
 */
const alertChip = (t: string): DetailChip => ({ t, bd: "#c0240f", fg: "#fff", bg: "#c0240f" });

function linkItem(source: CompendiumSource, type: CompendiumType, id: string): LinkItem {
  const r = source.raw(type, id);
  return {
    type,
    id,
    name: (r?.name as string) ?? id,
    kind: TYPE_SING[type],
    dot: source.accentOf(type, r?.source as string | undefined),
  };
}

/** The three keys lancer-data hangs a conditional rider off, in the order they're shown. */
const TRIGGER_KEYS = ["on_attack", "on_hit", "on_crit"] as const;

/** One raw `on_*` clause in its object form — `detail` prose plus whichever riders it carries. */
type RawTriggered = {
  detail?: string;
  activation?: string;
  damage?: (Damage & { aoe?: boolean; save?: string })[];
  save?: { stat?: string; aoe?: boolean };
};

/** One raw firing profile off `weapons.json`'s `profiles[]` (also a carrier of `on_*` clauses). */
type RawProfile = { name?: string; damage?: Damage[]; range?: RangeVal[]; effect?: string; tags?: TagRef[] };

/**
 * Read the `on_*` clauses off one raw record — a weapon, one of its profiles, or a mod. `effect`
 * is that record's own effect string: a clause whose prose merely repeats it (Thermal Charge
 * spells its detonator identically in both) keeps only its structured half, rather than printing
 * the same sentence twice on the same sheet.
 */
function triggeredClauses(raw: Record<string, unknown>, effect: string | undefined): TriggeredClauseView[] {
  const out: TriggeredClauseView[] = [];
  for (const key of TRIGGER_KEYS) {
    const v = raw[key];
    if (v == null) continue;
    const c: RawTriggered = typeof v === "string" ? { detail: v } : (v as RawTriggered);
    const quals: string[] = [];
    const qual = (q: string) => {
      if (!quals.includes(q)) quals.push(q);
    };
    // The gating conditions come off whichever field declares them: on the damage entries for a
    // save-or-take rider (Gravity Gun), on a bare `save` object for a save-or-be-moved one
    // (Magnetic Cannon, which forces a save but deals no damage).
    for (const dm of c.damage ?? []) {
      if (dm.save) qual(`${String(dm.save).toUpperCase()} SAVE`);
      if (dm.aoe) qual("AOE");
    }
    if (c.save?.stat) qual(`${c.save.stat.toUpperCase()} SAVE`);
    if (c.save?.aoe) qual("AOE");
    out.push({
      trigger: key.replace("_", " ").toUpperCase(),
      detailHtml: c.detail && c.detail !== effect ? c.detail : undefined,
      activation: c.activation,
      damage: (c.damage ?? []).map((x) => ({ type: String(x.type).toUpperCase(), val: x.val, col: DMG_COL[x.type] ?? "#111" })),
      quals,
    });
  }
  return out;
}

/**
 * A weapon's clauses, gathered across the record AND its firing profiles. The Siege Cannon repeats
 * one identical `on_attack` on both of its profiles (the sentence describes the *choice* between
 * the modes, not either mode), so identical profile clauses collapse to a single un-attributed
 * entry — the same hoist the shared-`effect` case does. A clause carried by only SOME profiles
 * keeps its profile name, so a mode-specific rider can never read as if it applied to the whole
 * weapon.
 */
function weaponTriggered(w: Record<string, unknown>, profiles: RawProfile[], effect: string | undefined): TriggeredClauseView[] {
  const out = triggeredClauses(w, effect);
  const groups = new Map<string, { clause: TriggeredClauseView; names: string[] }>();
  profiles.forEach((p, i) => {
    for (const clause of triggeredClauses(p as Record<string, unknown>, effect)) {
      const key = JSON.stringify([clause.trigger, clause.detailHtml ?? ""]);
      const g = groups.get(key) ?? { clause, names: [] };
      g.names.push(p.name ?? `Profile ${i + 1}`);
      groups.set(key, g);
    }
  });
  for (const { clause, names } of groups.values()) {
    if (names.length === profiles.length) out.push(clause);
    else for (const name of names) out.push({ ...clause, profile: name });
  }
  return out;
}

/**
 * Build the record sheet's view model for one ref.
 *
 * `tier` selects which column of an NPC record's per-tier data is shown — the stat array, the
 * weapon numbers, and the values interpolated into rules text all read it. It defaults to tier 1
 * so every existing caller is unchanged; wiring a real T1/T2/T3 selector through both hosts
 * (the archive window and `CompendiumRecordReader`) is items 3.2/3.3. It is inert for every
 * non-NPC record type.
 */
export function buildDetail(source: CompendiumSource, sel: Ref | null, tier: NpcTier = 1): Detail | null {
  if (!sel) return null;
  const { type } = sel;
  const it = source.raw(type, sel.id);
  if (!it) return null;

  const srcId = it.source as string | undefined;
  const accent = source.accentOf(type, type === "manufacturer" ? (it.id as string) : srcId);
  const tinted =
    type === "frame" ||
    type === "weapon" ||
    ((type === "system" || type === "mod" || type === "license") && srcId != null);

  const d: Detail = {
    kind: type,
    name: (it.name as string) ?? sel.id,
    pack: (it.pack as string) ?? "CORE",
    accent,
    tinted,
    ref: `${type.toUpperCase().replace("_", "-")} / ${sel.id.toUpperCase()}`,
    kicker: TYPE_SING[type],
    headBg: tinted ? accent : "#111",
    headFg: "#fff",
    headSub: tinted ? "rgba(255,255,255,.7)" : "#8a857a",
    chips: [],
  };

  const resolveTags = (arr: TagRef[] | undefined) => (arr ?? []).map((rf) => tagLabel(source, rf));

  if (type === "frame") {
    const f = it as unknown as Frame;
    const m = source.manufacturer(srcId);
    d.mfrId = srcId;
    d.mfrName = m?.name ?? srcId;
    // Chassis art from our local mirror in `public/frames/<frameId>.png`. FrameBody falls back
    // to the hatched CHASSIS RENDER placeholder if the frame has no mirrored image.
    d.artUrl = `/frames/${sel.id}.png`;
    d.chips = [
      ...(f.mechtype ?? []).map((x) => lightChip(String(x).toUpperCase())),
      filledChip(`LL ${f.license_level ?? 0}`),
    ];
    d.descHtml = f.description ?? "";
    const stats = f.stats as unknown as Record<string, number>;
    const cell = ([k, lab]: [string, string]): StatCell => ({ k: lab, v: stats?.[k] ?? 0 });
    const groups: [string, [string, string][]][] = [
      ["DURABILITY", [["hp", "HP"], ["armor", "ARMOR"], ["repcap", "REPAIR"]]],
      ["DEFENSES", [["evasion", "EVASION"], ["edef", "E-DEF"], ["save", "SAVE"]]],
      ["SYSTEMS", [["heatcap", "HEAT"], ["sensor_range", "SENSORS"], ["tech_attack", "TECH ATK"]]],
      ["CHASSIS", [["size", "SIZE"], ["speed", "SPEED"], ["sp", "SP"]]],
    ];
    d.statGroups = groups.map(([label, ks]) => ({ label, cells: ks.map(cell) }));
    d.statTracks = ([["structure", "STRUCTURE"], ["stress", "STRESS"]] as [string, string][]).map(cell);
    d.mounts = f.mounts ?? [];
  } else if (type === "weapon") {
    const w = it as unknown as Weapon;
    d.chips = [
      ...(w.mount ? [filledChip(w.mount.toUpperCase())] : []),
      ...(w.type ? [lightChip(w.type.replace("Heavy", " HVY").toUpperCase())] : []),
      lightChip(`SP ${w.sp ?? 0}`),
    ];
    d.damage = (w.damage as Damage[] | undefined ?? []).map((x) => ({
      type: String(x.type).toUpperCase(),
      val: x.val,
      col: DMG_COL[x.type] ?? "#111",
    }));
    d.range = (w.range as RangeVal[] | undefined ?? []).map((x) => ({ type: String(x.type).toUpperCase(), val: x.val }));
    d.tags = resolveTags(w.tags);
    // Weapons carry their rules text under `effect`, NOT `description` (19 of the 85 core weapons
    // have one; the four frame-integrated ones have ONLY that, no description at all) — surface it
    // the way the system/mod branch does, or those records show flavour with no rules, or nothing.
    d.effectHtml = (w as { effect?: string }).effect ?? "";
    // Multi-profile weapons keep their damage/range inside `profiles[]` (see `WeaponProfileView`).
    // Read them or the Assault Cannon / Leviathan / Siege Cannon / Apocalypse Rail / ZF4 SOLIDCORE
    // all render with empty DAMAGE and RANGE panels.
    const rawProfiles = (w as { profiles?: RawProfile[] }).profiles ?? [];
    // The ZF4 SOLIDCORE repeats one identical `effect` on all four of its Charge profiles (the
    // string describes the charge ladder as a whole, not any one step). Hoist a shared effect to
    // the record's own EFFECT section rather than printing it once per profile.
    const first = rawProfiles[0]?.effect;
    const sharedEffect = first && rawProfiles.every((p) => p.effect === first) ? first : undefined;
    if (sharedEffect && !d.effectHtml) d.effectHtml = sharedEffect;
    d.weaponProfiles = rawProfiles.map((p, i) => ({
      name: p.name ?? `Profile ${i + 1}`,
      damage: (p.damage ?? []).map((x) => ({ type: String(x.type).toUpperCase(), val: x.val, col: DMG_COL[x.type] ?? "#111" })),
      range: (p.range ?? []).map((x) => ({ type: String(x.type).toUpperCase(), val: x.val })),
      effectHtml: p.effect === sharedEffect ? undefined : p.effect,
      tags: resolveTags(p.tags),
    }));
    // The `on_*` riders (see `TriggeredClauseView`). The Gravity Gun's whole offensive output is
    // one of these — no top-level `damage` at all — so without this its DAMAGE panel is empty.
    d.triggered = weaponTriggered(it, rawProfiles, d.effectHtml || undefined);
    d.descHtml = w.description ?? "";
  } else if (type === "system" || type === "mod") {
    const sysmod = it as unknown as System & Mod;
    d.kicker = type === "mod" ? TYPE_SING.mod : TYPE_SING.system;
    const m = source.manufacturer(srcId);
    if (m) {
      d.mfrId = srcId;
      d.mfrName = m.name;
    }
    const chipBd = d.headBg === "#111" ? "#555" : "rgba(255,255,255,.45)";
    d.chips = [{ t: `SP ${sysmod.sp ?? 0}`, bd: chipBd, fg: "#fff", bg: "transparent" }];
    if (sysmod.license) {
      const lvl = sysmod.license_level != null ? ` ${sysmod.license_level}` : "";
      d.chips.push({ t: `${sysmod.license}${lvl}`.toUpperCase(), bd: chipBd, fg: "#fff", bg: "transparent" });
    }
    d.allowed = (sysmod as Mod).allowed_types ?? [];
    d.effectHtml = sysmod.effect ?? "";
    // Many systems/mods carry their rules under `actions[]` rather than a flat `effect` —
    // surface them so action-only items (e.g. StunCrown) aren't shown gutted. `detail` is
    // prose that already spells out any status/save the action applies.
    type RawAction = { name?: string; activation?: string; frequency?: string; trigger?: string; detail?: string };
    d.actions = ((sysmod as { actions?: RawAction[] }).actions ?? []).map((a) => ({
      name: a.name,
      activation: a.activation,
      frequency: a.frequency,
      triggerHtml: a.trigger,
      detailHtml: a.detail,
    }));
    // A few systems (the Walking Armory Ammo Cases) carry a menu of selectable ammunition under
    // `ammo[]`, and their `effect` string ends by pointing at it ("…one of the following effects
    // …at the listed cost"). Surface it or the record shows the pointer with nothing to point at.
    // lancer-data leaves `cost` off the base 1-charge options and only spells it out for the
    // 2-charge ones, so default it here.
    type RawAmmo = { name?: string; detail?: string; cost?: number };
    d.ammo = ((sysmod as { ammo?: RawAmmo[] }).ammo ?? []).map((a) => ({
      name: a.name ?? "",
      cost: a.cost ?? 1,
      detailHtml: a.detail,
    }));
    // One mod (Thermal Charge) carries an `on_hit` rider — the same shape weapons use, so it goes
    // through the same block rather than being dropped for want of a second code path.
    d.triggered = triggeredClauses(it, d.effectHtml || undefined);
    d.tags = resolveTags(sysmod.tags);
    d.descHtml = sysmod.description ?? "";
  } else if (type === "talent") {
    const t = it as unknown as Talent;
    d.chips = t.terse ? [darkChip(t.terse.toUpperCase())] : [];
    d.descHtml = t.description ?? "";
    d.ranks = (t.ranks ?? []).map((r, i) => ({
      roman: ROMAN[i + 1] ?? String(i + 1),
      name: r.name ?? "",
      descHtml: (r as { description?: string }).description ?? "",
    }));
  } else if (type === "license") {
    const m = source.manufacturer(srcId);
    d.mfrId = srcId;
    d.mfrName = m?.name ?? srcId;
    const unlocks = (it as unknown as { unlocks: { level: number; refs: Ref[] }[] }).unlocks;
    d.unlocks = unlocks.map((u) => ({
      rank: ROMAN[u.level] || "I",
      items: u.refs.map((rf) => linkItem(source, rf.type, rf.id)),
    }));
  } else if (type === "skill") {
    const sk = it as unknown as Skill;
    d.descHtml = sk.description ?? "";
    d.detailHtml = (sk as { detail?: string }).detail ?? "";
  } else if (type === "pilot_gear") {
    const g = it as unknown as PilotGear;
    d.chips = [darkChip((g.type ?? "GEAR").toUpperCase())];
    // `pilotGearProfile` (compendiumData.ts) is the single source both this card and the
    // pilot sheet's held-gear rows render — damage/range rows for weapons, `bonuses[]` rows
    // (replace/overwrite → `=N`, additive → `+N`) for armor. See its doc comment for why
    // pilot armor needs the bonuses[] half at all (it carries no damage/range).
    d.profile = pilotGearProfile(g);
    // Most pilot gear carries its rules under `effect`/`actions[]` (e.g. grenades) rather than a
    // flat `description` with top-level damage/range — surface both so action-only gear isn't shown
    // gutted, mirroring the system/mod branch above.
    d.effectHtml = (g as { effect?: string }).effect ?? "";
    type RawAction = { name?: string; activation?: string; frequency?: string; trigger?: string; detail?: string };
    d.actions = ((g as { actions?: RawAction[] }).actions ?? []).map((a) => ({
      name: a.name,
      activation: a.activation,
      frequency: a.frequency,
      triggerHtml: a.trigger,
      detailHtml: a.detail,
    }));
    d.tags = resolveTags(g.tags);
    d.descHtml = g.description ?? "";
  } else if (type === "core_bonus") {
    const cb = it as unknown as CoreBonus;
    const m = source.manufacturer(srcId);
    if (m) {
      d.mfrId = srcId;
      d.mfrName = m.name;
    }
    d.chips = srcId ? [darkChip(srcId.toUpperCase())] : [];
    d.effectHtml = cb.effect ?? "";
    d.mountedHtml = cb.mounted_effect ?? "";
    d.descHtml = cb.description ?? "";
  } else if (type === "tag") {
    d.descHtml = (it.description as string) ?? "";
    const refs: LinkItem[] = [];
    for (const tt of ["weapon", "system", "mod", "pilot_gear"] as CompendiumType[]) {
      for (const o of source.compendium.list(tt as never) as { id: string; name: string; source?: string; tags?: TagRef[] }[]) {
        if ((o.tags ?? []).some((rf) => rf.id === sel.id)) {
          refs.push({ type: tt, id: o.id, name: o.name, kind: TYPE_SING[tt], dot: source.accentOf(tt, o.source) });
        }
      }
    }
    d.refs = refs;
  } else if (type === "manufacturer") {
    const m = it as unknown as { id: string; name: string; quote?: string; description?: string };
    d.accent = mfrStyle(m.id).accent;
    d.headBg = d.accent;
    d.headFg = "#fff";
    d.headSub = "rgba(255,255,255,.7)";
    d.quoteHtml = m.quote ?? "";
    d.descHtml = m.description ?? "";
    const catalog: LinkItem[] = [];
    for (const tt of ["frame", "weapon", "system", "mod"] as CompendiumType[]) {
      for (const o of source.compendium.list(tt as never) as { id: string; name: string; source?: string }[]) {
        if (o.source === m.id) catalog.push({ type: tt, id: o.id, name: o.name, kind: TYPE_SING[tt], dot: d.accent });
      }
    }
    d.catalog = catalog;
  } else if (type === "background") {
    const b = it as unknown as Background;
    d.descHtml = b.description ?? "";
    d.bgSkills = (b.skills ?? []).map((id) => linkItem(source, "skill", id));
  } else if (type === "npc_class") {
    const c = it as unknown as NpcClass;
    d.npcRole = (c.role ?? "").toUpperCase();
    d.artUrl = npcClassArtUrl(sel.id);
    if (d.npcRole) d.chips = [darkChip(d.npcRole)];
    const info = c.info as { flavor?: string; tactics?: string } | undefined;
    const classStats = c.stats as Record<string, unknown> | undefined;
    d.npcFlavorHtml = interpolateTierTokens(info?.flavor ?? "", tier, classStats);
    d.npcTacticsHtml = interpolateTierTokens(info?.tactics ?? "", tier, classStats);
    d.npcTerse = interpolateTierTokens((info as { terse?: string } | undefined)?.terse ?? "", tier, classStats) || undefined;
    d.npcStats = npcStatArray(c.stats, tier);
    d.npcTier = tier;
    d.featureGroups = [
      { label: "BASE FEATURES", items: (c.base_features ?? []).map((id) => linkItem(source, "npc_feature", id)) },
      { label: "OPTIONAL FEATURES", items: (c.optional_features ?? []).map((id) => linkItem(source, "npc_feature", id)) },
    ].filter((g) => g.items.length > 0);
  } else if (type === "npc_template") {
    const t = it as unknown as NpcTemplate & { caveat?: unknown; tactics?: unknown };
    d.descHtml = interpolateTierTokens(t.description ?? "", tier);
    // A template's own `caveat`/`tactics` are real rules text with no other home on the sheet;
    // `tactics` reuses the class body's slot rather than inventing a second one.
    d.npcCaveatHtml = interpolateTierTokens(typeof t.caveat === "string" ? t.caveat : "", tier) || undefined;
    d.npcTacticsHtml = interpolateTierTokens(typeof t.tactics === "string" ? t.tactics : "", tier) || undefined;
    d.featureGroups = [
      { label: "BASE FEATURES", items: (t.base_features ?? []).map((id) => linkItem(source, "npc_feature", id)) },
      { label: "OPTIONAL FEATURES", items: (t.optional_features ?? []).map((id) => linkItem(source, "npc_feature", id)) },
    ].filter((g) => g.items.length > 0);
  } else if (type === "npc_feature") {
    // Cast, not a schema widening: every NPC schema is `.passthrough()`, so these fields already
    // survive parsing untouched — they are simply not in the declared shape.
    const f = it as unknown as NpcFeature & { effect?: string; origin?: unknown; actions?: unknown; deprecated?: unknown };
    d.npcType = (f.type ?? "").toUpperCase();
    if (d.npcType) d.chips = [darkChip(d.npcType)];
    // The badge leads the chip row: it qualifies the type chip beside it, so it has to be read
    // first. All five tombstones in the shipped pack ALSO carry "(DEPRECATED)" in their `name`,
    // so the browse row already reads correctly — this is the record sheet's own signal.
    d.npcDeprecated = (f as { deprecated?: unknown }).deprecated === true;
    if (d.npcDeprecated) d.chips = [alertChip("DEPRECATED"), ...d.chips];
    d.tags = resolveTags(f.tags);
    // A feature's embedded `{3/4/6}` / `{speed}` tokens resolve against the OWNING class's stats,
    // which only a v3 feature can reach: its `origin` is a real class id, where v2's is a display
    // object carrying a name. An unresolvable `{speed}` stays verbatim rather than blanking.
    const originStats = npcOriginStats(source, f.origin);
    const text = (raw: string | undefined): string => interpolateTierTokens(raw ?? "", tier, originStats);
    // NPC features carry rules text under `effect`; some packs use `description`.
    d.effectHtml = text(f.effect);
    d.descHtml = text(f.description);
    d.npcActions = npcActionViews(f.actions, text);
    d.npcWeapon = npcWeaponView(f, { tier, text });
    d.npcDeployables = npcDeployableViews((f as { deployables?: unknown }).deployables, { tier, text });
    d.npcTier = tier;
    const modId = (f as { mod?: unknown }).mod;
    if (typeof modId === "string" && source.raw("npc_feature", modId) !== undefined) {
      d.npcModifies = linkItem(source, "npc_feature", modId);
    }
  }

  return d;
}

/**
 * The `stats` of the class a feature belongs to, or `undefined` when it can't be reached.
 *
 * Only resolvable for a **v3** feature, whose `origin` is a real class id. v2 stores `origin` as
 * `{type, name, base}` — a display label ("ACE"), not an id — so there is nothing to look up, and
 * a v2 `{speed}` correctly stays verbatim. A feature owned by a *template* also returns
 * `undefined`: templates carry no stat block, which is the honest answer rather than a wrong one.
 */
function npcOriginStats(source: CompendiumSource, origin: unknown): Record<string, unknown> | undefined {
  if (typeof origin !== "string") return undefined;
  const owner = source.raw("npc_class", origin);
  const stats = owner?.stats;
  return stats !== null && typeof stats === "object" ? (stats as Record<string, unknown>) : undefined;
}

/**
 * Flatten a v3 feature's `actions[]` into {@link NpcActionView}s, dropping entries that carry
 * nothing renderable. `text` is the caller's tier-interpolating formatter, passed in so the
 * tier is resolved once at the call site rather than threaded through every field here.
 */
function npcActionViews(
  actions: unknown,
  text: (raw: string | undefined) => string,
): NpcActionView[] | undefined {
  if (!Array.isArray(actions)) return undefined;
  const out: NpcActionView[] = [];
  for (const raw of actions) {
    if (raw === null || typeof raw !== "object") continue;
    const a = raw as Record<string, unknown>;
    const str = (k: string): string | undefined => (typeof a[k] === "string" ? (a[k] as string) : undefined);
    const view: NpcActionView = {
      name: str("name"),
      activation: str("activation"),
      frequency: str("frequency"),
      trigger: text(str("trigger")) || undefined,
      detailHtml: text(str("detail")) || undefined,
      effectHtml: text(str("effect")) || undefined,
    };
    // An action with no name and no text would render as an empty box — worse than omitting it.
    if (view.name || view.detailHtml || view.effectHtml || view.trigger) out.push(view);
  }
  return out.length > 0 ? out : undefined;
}

/** Canonical stat display order + labels. Any extra key a pack carries falls in after these. */
const NPC_STAT_ORDER: readonly (readonly [string, string])[] = [
  ["hp", "HP"], ["armor", "ARMOR"], ["evade", "EVADE"], ["edef", "E-DEF"], ["heatcap", "HEAT"],
  ["speed", "SPEED"], ["sensor", "SENSORS"], ["save", "SAVE"], ["size", "SIZE"],
  ["activations", "ACTIVATIONS"], ["hull", "HULL"], ["agility", "AGI"], ["systems", "SYS"], ["engineering", "ENG"],
];

/**
 * Normalize an NPC class's `stats` into one labelled row per stat, resolved at `tier`.
 *
 * **A SCALAR STAT IS TIER-INVARIANT, NOT MISSING — and reading it as missing was a live defect.**
 * This function used to `continue` past any non-array value, which is invisible on a v2 pack
 * (v2 arrays all 14 stats on all 33 classes) and severe on v3, which collapses a stat that does
 * not scale to a bare scalar. Measured over the shipped v3 pack, that dropped the row entirely
 * for `activations` on 33 of 33 classes, `sensor` on 28, `size` on 28, `armor` on 27, `heatcap`
 * on 24, `speed` on 18, `edef` on 13, `hull` on 12, and **`hp` on 8** — the Ace's sheet showed 8
 * of its 14 stats, with no armor, heat, sensors, size, activations or hull, and eight classes
 * showed no HP at all. Found by the Phase 2 field inventory, fixed here at the READER rather than
 * by normalizing at ingest: the v3 adapter already reconstructs the feature edges on the way in,
 * and a second archive-rewriting step would put two competing answers in the repo about what the
 * data is. `statAtTier` is the shared reader that encodes tier-invariance, so the stat array, the
 * weapon block and the `{speed}` token all agree by construction.
 *
 * `varies` marks a stat whose value differs across tiers, so the sheet can show which numbers the
 * tier selector actually moves.
 */
function npcStatArray(stats: unknown, tier: NpcTier): NpcStatRow[] {
  if (stats === null || typeof stats !== "object") return [];
  const s = stats as Record<string, unknown>;
  const known = new Set(NPC_STAT_ORDER.map(([k]) => k));
  const keys = [
    ...NPC_STAT_ORDER.map(([k]) => k).filter((k) => k in s),
    ...Object.keys(s).filter((k) => !known.has(k)),
  ];
  const labelOf = (k: string): string => NPC_STAT_ORDER.find(([kk]) => kk === k)?.[1] ?? k.toUpperCase();

  const rows: NpcStatRow[] = [];
  for (const k of keys) {
    const at = statAtTier(s[k], tier);
    if (at === undefined) continue;
    const across = NPC_TIERS.map((t) => statAtTier(s[k], t));
    rows.push({ k: labelOf(k), v: String(at), varies: new Set(across.map(String)).size > 1 });
  }
  return rows;
}
